import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
  faEye,
  faEdit,
  faTrash,
  faBuilding,
  faToggleOn,
  faToggleOff,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import DataTable from '../common/DataTable';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';


/**
 * getStatusBadge(isActive)
 *
 * Small helper that returns a styled status pill for active/inactive
 * customers. Used inside the table `renderCell` for the status column.
 * Receives the translation function `t` from the calling component.
 */
const getStatusBadge = (isActive, t) => (
  <span
    className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold"
    style={{
      backgroundColor: colorHelpers.withOpacity(
        isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR,
        0.1
      ),
      color: isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR,
    }}
  >
    <FontAwesomeIcon icon={isActive ? faCheck : faTimes} />
    {isActive ? t('customers.statuses.active') : t('customers.statuses.inactive')}
  </span>
);

/**
 * CustomersTable
 *
 * Wrapper around the generic `DataTable` that configures columns, row
 * styling and actions specific to the customers list page.
 *
 * Props include handler callbacks for selection, viewing, editing,
 * deleting and toggling customer status.
 */
const CustomersTable = ({
  customers = [],
  loading = false,
  selectedCustomers = [],
  onSelectCustomer,
  onSelectAll,
  onViewCustomer,
  onEditCustomer,
  onDeleteCustomer,
  onToggleStatus,
  onViewWarehouses,
  viewLoading = false,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess, checkEditAccess } = useRole();
  const hasCustomers = Array.isArray(customers) && customers.length > 0;
  const selectedSet = useMemo(() => new Set(selectedCustomers), [selectedCustomers]);
  const allSelected = hasCustomers && selectedCustomers.length === customers.length;

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.4),
    []
  );
  const selectedColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.12),
    []
  );
  const selectionAccent = UI_COLORS.BUTTONS.PRIMARY.background;

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        label: t('customers.table.columns.select'),
        width: '60px',
        align: 'center',
        alignItems: 'center',
        sortable: false,
        renderHeader: ({ loading: isLoading }) => (
          <input
            type="checkbox"
            aria-label={t('customers.table.columns.select')}
            checked={allSelected}
            onChange={() => onSelectAll?.(!allSelected)}
            disabled={!hasCustomers || isLoading}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        renderCell: (customer) => (
          <input
            type="checkbox"
            aria-label={`${t('customers.table.columns.select')} ${customer.tallyName || ''}`}
            checked={selectedSet.has(customer.id)}
            onChange={() => onSelectCustomer?.(customer.id)}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        skeleton: <div className="h-4 w-4 rounded bg-gray-300" />,
      },
      {
        id: 'tallyName',
        label: t('customers.table.columns.company'),
        sortable: true,
        align: 'center',
        sortAccessor: (customer) => (customer?.tallyName || '').toLowerCase(),
        renderCell: (customer) => (
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.15),
                color: UI_COLORS.BUTTONS.PRIMARY.background,
              }}
            >
              {(customer.tallyName)?.[0]?.toUpperCase() || 'C'}
            </div>
            <div>
              <div className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {customer.tallyName || t('common.symbols.emDash')}
              </div>
              {customer.companyName && customer.companyName !== customer.tallyName && (
                <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {customer.companyName}
                </p>
              )}
            </div>
          </div>
        ),
      },
      {
        id: 'customerType',
        label: t('customers.table.columns.type'),
        sortable: true,
        renderCell: (customer) => (
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.2),
              color: UI_COLORS.TEXT.PRIMARY,
            }}
          >
            {customer.customerType}
          </span>
        ),
      },
      {
        id: 'location',
        label: t('customers.table.columns.location'),
        sortable: true,
        sortAccessor: (customer) => `${customer?.city || ''}|${customer?.state || ''}`.toLowerCase(),
        renderCell: (customer) => (
          <div className="flex flex-col text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            <span>{customer.city || t('common.symbols.emDash')}</span>
            <span className="text-xs opacity-75">{customer.state || t('common.symbols.emDash')}</span>
          </div>
        ),
      },
      {
        id: 'warehouses',
        label: t('commonCols.warehouses'),
        align: 'center',
        width: '120px',
        sortable: false,
        renderCell: (customer) => (
          <button
            type="button"
            onClick={() => onViewWarehouses?.(customer)}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
              color: UI_COLORS.BUTTONS.PRIMARY.text,
            }}
          >
            <FontAwesomeIcon icon={faWarehouse} />
            Warehouse
          </button>
        ),
        skeleton: <div className="h-6 w-20 rounded bg-gray-200 mx-auto" />,
      },
      {
        id: 'status',
        label: t('customers.table.columns.status'),
        align: 'center',
        justify: 'center',
        sortable: true,
        sortAccessor: (customer) => (customer.isActive ? 1 : 0),
        renderCell: (customer) => getStatusBadge(customer.isActive, t),
      },
      {
        id: 'actions',
        label: t('customers.table.columns.actions'),
        align: 'center',
        width: '160px',
        sortable: false,
        renderCell: (customer) => (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => onViewCustomer?.(customer.id)}
              className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1),
                color: UI_COLORS.NAVIGATION.ACTIVE,
              }}
              title={t('customers.table.tooltips.view')}
              disabled={Boolean(viewLoading)}
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
            {checkEditAccess(customer.createdAt) && (
              <button
                type="button"
                onClick={() => onEditCustomer?.(customer.id)}
                className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.12),
                  color: UI_COLORS.BUTTONS.PRIMARY.background,
                }}
                title={t('customers.table.tooltips.edit')}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            <button
              type="button"
              onClick={() => onToggleStatus?.(customer.id, !customer.isActive)}
              className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105"
              style={{
                color: customer?.isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.WARNING,
                backgroundColor: colorHelpers.withOpacity(
                  customer?.isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.WARNING,
                  0.1
                ),
              }}
              title={customer.isActive ? t('customers.table.tooltips.deactivate') : t('customers.table.tooltips.activate')}
            >
              <FontAwesomeIcon icon={customer.isActive ? faToggleOff : faToggleOn} />
            </button>
            {checkDeleteAccess() && (
              <button
                type="button"
                onClick={() => onDeleteCustomer?.(customer.id)}
                className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.12),
                  color: UI_COLORS.STATUS.ERROR,
                }}
                title={t('customers.table.tooltips.delete')}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        ),
      },
    ],
    [
      allSelected,
      hasCustomers,
      onDeleteCustomer,
      onEditCustomer,
      onSelectAll,
      onSelectCustomer,
      onToggleStatus,
      onViewCustomer,
      onViewWarehouses,
      selectedSet,
      selectionAccent,
      viewLoading,
      checkDeleteAccess,
      checkEditAccess,
      t,
    ]
  );

  /**
   * getRowProps(customer)
   *
   * Callback passed to `DataTable` to derive per-row props. Adds hover
   * styling and persistent highlight when a row is selected.
   */
  const getRowProps = useCallback(
    (customer) => {
      const isSelected = selectedSet.has(customer.id);
      return {
        className: 'transition-colors',
        style: {
          backgroundColor: isSelected ? selectedColor : 'transparent',
        },
        onMouseEnter: (event) => {
          if (!isSelected) {
            event.currentTarget.style.backgroundColor = hoverColor;
          }
        },
        onMouseLeave: (event) => {
          event.currentTarget.style.backgroundColor = isSelected ? selectedColor : 'transparent';
        },
      };
    },
    [hoverColor, selectedColor, selectedSet]
  );

  const emptyState = useMemo(
    () => ({
      icon: faBuilding,
      title: t('customers.table.emptyState.defaultTitle'),
      message: t('customers.table.emptyState.defaultMessage'),
    }),
    [t]
  );

  return (
    <DataTable
      columns={columns}
      data={Array.isArray(customers) ? customers : []}
      loading={loading}
      keyField="id"
      enablePagination={false}
      defaultSort={{ field: 'tallyName', direction: 'asc' }}
      getRowProps={getRowProps}
      emptyState={emptyState}
    />
  );
};

export default CustomersTable;
